import React, { useState, useRef, useEffect } from 'react';
import { auth, storage } from '../lib/firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import useChatStore from '../store/chatStore';

const ChatBox = (props) => {
  const [message, setMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadTask, setUploadTask] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [invalidFileTypeError, setInvalidFileTypeError] = useState(false);
  const [slowUploadWarning, setSlowUploadWarning] = useState(false); // State for slow upload warning
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const {
    postUserChat,
    postUploadedPic,
    postUploadedVideo,
    replyToMsg,
    setReplyToMsg,
  } = useChatStore((state) => ({
    postUserChat: state.postUserChat,
    postUploadedPic: state.postUploadedPic,
    postUploadedVideo: state.postUploadedVideo,
    replyToMsg: state.replyToMsg,
    setReplyToMsg: state.setReplyToMsg,
  }));

  const scrollTo = props.scrollTo;
  const [attachmentToggleState, setAttachmentToggleState] = useState(false);
  const messageInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const allowedImageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
  const allowedVideoExtensions = ['mp4', 'webm'];

  useEffect(() => {
    if (replyToMsg && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [replyToMsg]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getFileExtension = (fileName) => {
    return fileName
      .slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2)
      .toLowerCase();
  };

  const isFileExtensionAllowed = (file, type) => {
    const extension = getFileExtension(file.name);
    if (type === 'image') {
      return allowedImageExtensions.includes(extension);
    } else if (type === 'video') {
      return allowedVideoExtensions.includes(extension);
    }
    return false;
  };

  const resetUploadState = () => {
    setUploadedFile(null);
    setProgress(0);
    setUploadTask(null);
    setFileType(null);
    setShowCancelButton(false);
    setSlowUploadWarning(false); // Reset slow upload warning
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    setAttachmentToggleState(false);
  };

  const calculateUploadSpeed = (bytesTransferredSinceLast, timeElapsed) => {
    return bytesTransferredSinceLast / timeElapsed; // bytes per second
  };

  const uploadFile = (file, type) => {
    setAttachmentToggleState(false);

    const storageRef = ref(
      storage,
      `uploads/${Date.now()}_${auth.currentUser.uid}.${getFileExtension(
        file.name
      )}`
    );

    const task = uploadBytesResumable(storageRef, file);
    setUploadTask(task);

    let lastBytesTransferred = 0;
    let lastTimestamp = Date.now();

    task.on(
      'state_changed',
      (snapshot) => {
        const progressPercentage =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progressPercentage);

        const currentTime = Date.now();
        const bytesTransferredSinceLast =
          snapshot.bytesTransferred - lastBytesTransferred;
        const timeElapsed = (currentTime - lastTimestamp) / 1000; // in seconds

        const uploadSpeed = calculateUploadSpeed(
          bytesTransferredSinceLast,
          timeElapsed
        );

        lastBytesTransferred = snapshot.bytesTransferred;
        lastTimestamp = currentTime;

        const speedThreshold = 1024 * 1024; // 100 KB/s
        if (uploadSpeed < speedThreshold) {
          setSlowUploadWarning(true);
        } else {
          setSlowUploadWarning(false);
        }
      },
      (error) => {
        console.error('Error uploading file:', error);
        resetUploadState();
      },
      () => {
        if (type === 'image') {
          postUploadedPic(auth.currentUser.uid, task.snapshot, scrollTo);
        } else if (type === 'video') {
          postUploadedVideo(auth.currentUser.uid, task.snapshot, scrollTo);
        }
        resetUploadState();
      }
    );
  };

  const cancelUpload = () => {
    if (uploadTask) {
      uploadTask.cancel();
    }
    resetUploadState();
  };

  const handleFileUpload = (e, type) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (isFileExtensionAllowed(selectedFile, type)) {
        setUploadedFile(selectedFile);
        setFileType(type);
        uploadFile(selectedFile, type);
      } else {
        let errorMsg = `Invalid file type. Allowed extensions for ${type} are: ${
          type === 'image'
            ? allowedImageExtensions.join(', ')
            : allowedVideoExtensions.join(', ')
        }`;

        setInvalidFileTypeError(errorMsg);

        setTimeout(() => {
          setInvalidFileTypeError(false);
        }, 3000);
      }
    }
  };

  const handleChatMessagePost = async (e) => {
    e.preventDefault();
    e.target.reset();

    if (message.trim() === '') {
      return false;
    }

    let msgPayload = {
      uid: auth.currentUser.uid,
      message,
    };

    if (replyToMsg) {
      Object.assign(msgPayload, { replyTo: replyToMsg });
    }

    setMessage('');
    setReplyToMsg(null);
    postUserChat(msgPayload, scrollTo);
  };

  const attachFileTrigger = (e) => {
    e.preventDefault();
    setReplyToMsg(null);
    setAttachmentToggleState(!attachmentToggleState);
  };

  const attachmentOptionsRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        attachmentOptionsRef.current &&
        !attachmentOptionsRef.current.contains(event.target) &&
        !event.target.classList.contains('attach-file-trigger')
      ) {
        setAttachmentToggleState(false);
      }
    }

    document.body.addEventListener('click', handleClickOutside);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div className="chatbox-wrapper">
      {invalidFileTypeError && (
        <div className="error-notification">{invalidFileTypeError}</div>
      )}

      {isOffline && (
        <div className="error-notification">Internet Disconnected</div>
      )}

      {slowUploadWarning && (
        <div className="slow-upload-warning">
          Upload speed is slow. Please check your connection.
        </div>
      )}

      <div
        ref={attachmentOptionsRef}
        className={`attachment-options ${
          attachmentToggleState && !uploadTask ? '' : 'hide'
        }`}
      >
        <form className="attachment-upload-img">
          <input
            ref={imageInputRef}
            type="file"
            id="image-upload"
            accept=".png, .jpg, .jpeg, .gif, .webp"
            onChange={(e) => handleFileUpload(e, 'image')}
          />
          <label htmlFor="image-upload">
            <span className={`material-icons ${attachmentToggleState}`}>
              image
            </span>
          </label>
        </form>
        <form className="attachment-upload-video">
          <input
            ref={videoInputRef}
            type="file"
            id="video-upload"
            accept=".mp4, .webm"
            onChange={(e) => handleFileUpload(e, 'video')}
          />
          <label htmlFor="video-upload">
            <span className={`material-icons ${attachmentToggleState}`}>
              videocam
            </span>
          </label>
        </form>
      </div>
      {uploadTask ? (
        <div
          className="upload-progress-container"
          onMouseEnter={() => setShowCancelButton(true)}
          onMouseLeave={() => setShowCancelButton(false)}
        >
          <svg className="progress-ring" width="44" height="44">
            <circle
              className="progress-ring__circle"
              stroke="black"
              strokeWidth="2"
              fill="transparent"
              r="20"
              cx="22"
              cy="22"
            />
            <circle
              className="progress-ring__circle progress-ring__circle--progress"
              stroke="#2ecc71"
              strokeWidth="4"
              fill="transparent"
              r="20"
              cx="22"
              cy="22"
              style={{
                strokeDasharray: `${2 * Math.PI * 22}`,
                strokeDashoffset: `${2 * Math.PI * 22 * (1 - progress / 100)}`,
              }}
            />
          </svg>
          {showCancelButton ? (
            <button className="cancel-upload-button" onClick={cancelUpload}>
              <svg viewBox="0 0 48 48">
                <defs id="defs7">
                  <linearGradient id="linearGradient828">
                    <stop
                      style={{ stopColor: '#ff0000', stopOpacity: 1 }}
                      offset="0"
                      id="stop826"
                    />
                  </linearGradient>
                  <linearGradient
                    id="0"
                    gradientUnits="userSpaceOnUse"
                    y1="47.37"
                    x2="0"
                    y2="-1.429"
                  >
                    <stop stop-color="#c52828" id="stop2" />
                    <stop offset="1" stop-color="#ff5454" id="stop4" />
                  </linearGradient>
                </defs>
                <g
                  transform="matrix(.99999 0 0 .99999-58.37.882)"
                  enable-background="new"
                  id="g13"
                  style={{ fillOpacity: 1 }}
                >
                  <circle
                    cx="82.37"
                    cy="23.12"
                    r="24"
                    fill="url(#0)"
                    id="circle9"
                    style={{ fillOpacity: 1, fill: '#dd3333' }}
                  />
                  <path
                    d="m87.77 23.725l5.939-5.939c.377-.372.566-.835.566-1.373 0-.54-.189-.997-.566-1.374l-2.747-2.747c-.377-.372-.835-.564-1.373-.564-.539 0-.997.186-1.374.564l-5.939 5.939-5.939-5.939c-.377-.372-.835-.564-1.374-.564-.539 0-.997.186-1.374.564l-2.748 2.747c-.377.378-.566.835-.566 1.374 0 .54.188.997.566 1.373l5.939 5.939-5.939 5.94c-.377.372-.566.835-.566 1.373 0 .54.188.997.566 1.373l2.748 2.747c.377.378.835.564 1.374.564.539 0 .997-.186 1.374-.564l5.939-5.939 5.94 5.939c.377.378.835.564 1.374.564.539 0 .997-.186 1.373-.564l2.747-2.747c.377-.372.566-.835.566-1.373 0-.54-.188-.997-.566-1.373l-5.939-5.94"
                    fill="#fff"
                    fill-opacity=".842"
                    id="path11"
                    style={{ fillOpacity: 1, fill: '#ffffff' }}
                  />
                </g>
              </svg>
            </button>
          ) : (
            <div className="progress-text">{Math.round(progress)}%</div>
          )}
        </div>
      ) : (
        <a
          href="/#"
          className={`attach-file-trigger ${
            attachmentToggleState ? 'active-trigger' : ''
          }`}
          onClick={attachFileTrigger}
        >
          <span className="material-icons">attach_file</span>
        </a>
      )}
      <form onSubmit={handleChatMessagePost} className="send-msg-form">
        <input
          type="text"
          autoFocus={true}
          spellCheck={false}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message here :)"
          autoComplete="off"
          ref={messageInputRef}
        />
        <button type="submit">
          <span className="material-icons">send</span>
        </button>
      </form>
    </div>
  );
};

export default ChatBox;
